import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { Twitter, Linkedin, Instagram, Youtube, Facebook, Mail, GraduationCap, Presentation } from "lucide-react";
import { reopenConsentBanner } from "@/lib/cookieConsent";

// ── Compliance review (footer-compliance-review) ──────────────────────────────
// Trust badges hidden pending certification. System status hidden — not wired
// to real monitoring. Social links restored with placeholder hrefs; replace with
// real profile URLs before launch.
const SHOW_TRUST_BADGES = false;
const SHOW_SYSTEM_STATUS = false;

// Brand colours (SCRUM-356): each icon sits on its network's own fill, white glyph.
const socials = [
  { icon: Instagram, href: "https://www.instagram.com/bookmycoaching", label: "Instagram", bg: "bg-[linear-gradient(45deg,#F58529,#DD2A7B,#8134AF,#515BD4)]" },
  { icon: Youtube, href: "https://www.youtube.com/@bookmycoaching", label: "YouTube", bg: "bg-[#FF0000]" },
  { icon: Linkedin, href: "https://www.linkedin.com/company/bookmycoaching", label: "LinkedIn", bg: "bg-[#0A66C2]" },
  { icon: Facebook, href: "https://www.facebook.com/bookmycoaching", label: "Facebook", bg: "bg-[#1877F2]" },
  { icon: Twitter, href: "https://twitter.com/bookmycoaching", label: "Twitter", bg: "bg-[#1DA1F2]" },
];

const POPULAR_SEARCHES = [
  { label: "Online maths tutor", to: "/search?q=maths" },
  { label: "Class 12 crash course", to: "/exam-crash-courses" },
  { label: "JEE crash course", to: "/search?q=JEE+crash+course" },
  { label: "NEET group classes", to: "/search?q=NEET+group" },
  { label: "Class 10 maths group tuition", to: "/search?q=class+10+maths" },
  { label: "Spoken English classes online", to: "/search?q=spoken+english" },
  { label: "One-on-one tutoring", to: "/private-tutoring-sessions" },
  { label: "Online private tutor", to: "/private-tutoring-sessions" },
  { label: "Board exam crash course", to: "/exam-crash-courses" },
  { label: "Coding classes for kids", to: "/search?q=coding+kids" },
];

const columns = [
  {
    heading: "Learn",
    items: [
      { label: "1-on-1 Tutors", to: "/private-tutoring-sessions" },
      { label: "Group Classes", to: "/online-group-classes" },
      { label: "Crash Courses", to: "/exam-crash-courses" },
      { label: "Search all tutors", to: "/search" },
      { label: "How it works", to: "/how-it-works" },
      { label: "Pricing", to: "/pricing" },
    ],
  },
  {
    heading: "Popular Subjects",
    items: [
      { label: "Maths", to: "/search?subject=maths" },
      { label: "Science", to: "/search?subject=science" },
      { label: "English", to: "/search?subject=english" },
      { label: "Spoken English", to: "/search?subject=spoken-english" },
      { label: "Coding", to: "/search?subject=coding" },
      { label: "All subjects", to: "/all-subjects" },
    ],
  },
  {
    heading: "Exam Preparation",
    items: [
      { label: "Class 10 & 12 Boards", to: "/search?exam=boards" },
      { label: "JEE", to: "/search?exam=JEE" },
      { label: "NEET", to: "/search?exam=NEET" },
      { label: "UPSC", to: "/search?exam=UPSC" },
      { label: "CAT", to: "/search?exam=CAT" },
      { label: "GATE", to: "/search?exam=GATE" },
    ],
  },
  {
    heading: "For Teachers",
    items: [
      { label: "Become an Online Tutor", to: "/become-an-online-tutor" },
      { label: "Gold & Platinum Plans", to: "/pricing" },
      { label: "Tutor Login", to: "/login" },
      { label: "How It Works", to: "/how-it-works/apply" },
    ],
  },
  {
    heading: "Company",
    items: [
      { label: "About Us", to: "/about" },
      { label: "Blog", to: "/blog" },
      { label: "Contact Us", to: "/contact" },
    ],
  },
];

const legal = [
  { label: "Terms of Service", to: "/terms" },
  { label: "Privacy Policy", to: "/privacy" },
  // Refund policy link hidden pending legal sign-off on the cancellation stance.
  // Re-enable once the payments & refunds page is approved.
  // { label: "Refund Policy", to: "/terms#cancellations" },
];

export function PreFooter() {
  return (
    <section className="bg-[#FFF1D6] py-16">
      <div className="container mx-auto px-4 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Card 1: Looking for a tutor */}
        <div className="bg-white rounded-2xl shadow-sm p-8 flex items-start gap-5">
          <div className="w-12 h-12 rounded-xl bg-[hsl(237,52%,31%)] flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-7 h-7 text-[#F7B030]" />
          </div>
          <div className="flex flex-col gap-3">
            <h3 className="text-lg font-bold text-[hsl(237,52%,15%)]">Looking for a tutor?</h3>
            <p className="text-sm text-[hsl(237,30%,40%)] leading-relaxed">
              Compare 1-on-1 tutors, group batches and crash courses, then send an enquiry.
            </p>
            <Link
              to="/search"
              className="inline-flex items-center justify-center self-start text-sm font-semibold bg-[#F7B030] hover:bg-[hsl(39,96%,42%)] text-[hsl(237,52%,15%)] px-5 py-2.5 rounded-full transition-colors"
            >
              Find a tutor
            </Link>
          </div>
        </div>

        {/* Card 2: Want to teach */}
        <div className="bg-white rounded-2xl shadow-sm p-8 flex items-start gap-5">
          <div className="w-12 h-12 rounded-xl bg-[#F7B030] flex items-center justify-center flex-shrink-0">
            <Presentation className="w-7 h-7 text-[hsl(237,52%,15%)]" />
          </div>
          <div className="flex flex-col gap-3">
            <h3 className="text-lg font-bold text-[hsl(237,52%,15%)]">Want to teach?</h3>
            <p className="text-sm text-[hsl(237,30%,40%)] leading-relaxed">
              Create a teacher profile, publish your classes and set your own fees.
            </p>
            <Link
              to="/become-an-online-tutor"
              className="inline-flex items-center justify-center self-start text-sm font-semibold border border-[hsl(237,52%,31%)] text-[hsl(237,52%,31%)] hover:bg-[hsl(237,52%,31%)] hover:text-white px-5 py-2.5 rounded-full transition-colors"
            >
              Become a Tutor
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}

export function Footer() {
  return (
    <>
    <PreFooter />
    <footer className="bg-[hsl(237,52%,15%)] text-[hsl(237,30%,75%)]">

      {/* ── Main grid ── */}
      <div className="container mx-auto px-4 pt-14 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-10 pb-10 border-b border-white/10">

          {/* Brand column — spans 2 of 7 on lg */}
          <div className="lg:col-span-2">
            <Logo variant="on-dark" />
            <p className="mt-4 mb-5 text-sm leading-relaxed max-w-xs text-[hsl(237,20%,70%)]">
              Find reviewed tutors, group batches and exam crash courses across India, online or near you.
            </p>

            {/* Social icons */}
            <div className="flex items-center gap-2 mb-5">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-white shadow-sm hover:scale-110 hover:brightness-110 transition-all duration-200 ${s.bg}`}
                >
                  <s.icon className="w-4 h-4" />
                </a>
              ))}
            </div>

            {/* Contact */}
            <div className="flex flex-col gap-2 text-sm">
              <a href="mailto:support@bookmycoaching.com" className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail className="w-4 h-4 text-[hsl(39,96%,48%)] flex-shrink-0" />
                support@bookmycoaching.com
              </a>
            </div>

            {SHOW_TRUST_BADGES && (
              <div className="flex flex-wrap gap-2 mt-5">
                {["SSL Secured", "GDPR Ready", "ISO 27001"].map((badge) => (
                  <span key={badge} className="text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full border border-white/20 text-white/50">
                    {badge}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Link columns — 5 of 7 on lg */}
          <div className="lg:col-span-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8">
            {columns.map((col) => (
              <div key={col.heading}>
                <p className="text-xs font-semibold uppercase tracking-widest text-white mb-4">{col.heading}</p>
                <ul className="space-y-2.5">
                  {col.items.map((item) => (
                    <li key={item.label}>
                      <Link
                        to={item.to}
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        className="text-sm hover:text-white transition-colors"
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* ── Popular searches (SEO internal links) ── */}
        <div className="py-6 border-b border-white/10">
          <p className="text-xs font-semibold uppercase tracking-widest text-white mb-3">Popular searches</p>
          <div className="flex flex-wrap gap-x-5 gap-y-1.5">
            {POPULAR_SEARCHES.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="text-sm hover:text-white transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="pt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-[hsl(237,20%,60%)]">
          <p>© {new Date().getFullYear()} BookMyCoaching Pvt. Ltd. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
            {legal.map((item) => (
              <Link key={item.label} to={item.to} className="hover:text-white transition-colors">
                {item.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={reopenConsentBanner}
              className="hover:text-white transition-colors cursor-pointer"
            >
              Cookie Preferences
            </button>
            {SHOW_SYSTEM_STATUS && (
              <span className="flex items-center gap-1.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1A8A6E] opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#1A8A6E]" />
                </span>
                All systems operational
              </span>
            )}
            <span>Noida, India 🇮🇳</span>
          </div>
        </div>
      </div>

    </footer>
    </>
  );
}
